// ── "someone changed this while your drawer was open" ────────────────────────
//
// The browser half of the lost-update control. Two stations work the same visit
// all day and the day lock deliberately keeps TODAY's rows writable for
// everyone, so a drawer open on a record another station is editing is the
// normal case. This module answers two questions with the same comparator:
//
//   BEFORE the save — did the row move underneath me? Driven off the realtime
//   event the page already receives, so the drawer can say so while the user is
//   still reading, rather than at บันทึก.
//
//   AT the save — the server answers 409 `stale_record` and hands back the
//   fields that moved. That is the AUTHORITATIVE answer: it sees writes this
//   page never heard about, including Bubble's, which is still production and
//   broadcasts nothing this app subscribes to.
//
// ⚠️ MIRRORS functions/api/_conflict.js — read that file for why the row's own
// values are the version token and not `modified_timestamp` (the column exists,
// nothing has ever written it: 0 of 1,364,749 rows, measured 2026-08-19). The
// two copies must agree on `sameValue`, or the banner and the 409 will disagree
// about what counts as a change and the drawer will warn about a save the server
// then accepts. Duplicated rather than imported because functions/ and src/ are
// separate bundles — the same call `dayLocked` makes.

export const BILLING_WATCH = [
  'billing_type_id', 'price', 'N', 'detail', 'priceadvance', 'claimRef', 'advancePay',
  'priceXunit', 'priceDoctor', 'priceClinic', 'priceActualTotal', 'priceActualClinic',
  'percentDoctor', 'active',
]

export const TREATMENT_WATCH = ['detail', 'remark', 'auto_fee', 'appointmentlist_id', 'doctor_id']

const truthy = (v) => v === true || v === 1 || v === '1' || v === 'true'
const blank = (v) => v === null || v === undefined || String(v).trim() === ''

// A decimal column comes back as `427.50` from one feed and 427.5 from another,
// an int as "3" or 3, a bool as true or 1 — comparing those as text would report
// a conflict on every save. Numbers compare as numbers, bools as bools, and only
// genuinely textual values as trimmed text.
export function sameValue(a, b) {
  if (typeof a === 'boolean' || typeof b === 'boolean') return truthy(a) === truthy(b)
  const sa = String(a ?? '').trim()
  const sb = String(b ?? '').trim()
  if (sa === sb) return true
  const na = Number(sa)
  const nb = Number(sb)
  if (sa !== '' && sb !== '' && Number.isFinite(na) && Number.isFinite(nb)) return na === nb
  return false
}

// Only keys present on `expected` are checked, and a key it holds as null/'' is
// skipped — the client cannot tell "this row has no value" from "this feed omits
// the key", and treating empty as a real value would warn about saves where
// nothing happened. See the server copy for the trade-off this accepts.
export function conflictingFields(expected, current, watch) {
  if (!expected || typeof expected !== 'object' || !current || typeof current !== 'object') return []
  const out = []
  for (const k of watch) {
    if (!Object.prototype.hasOwnProperty.call(expected, k)) continue
    // A key the current row does not carry is one this feed does not report —
    // unknown, not changed. See the server copy for why this matters: the two
    // sides are different endpoints with different output whitelists.
    if (!Object.prototype.hasOwnProperty.call(current, k)) continue
    const was = expected[k]
    if (blank(was)) continue
    if (!sameValue(was, current[k])) out.push({ field: k, was, now: current[k] ?? null })
  }
  return out
}

/** The `expected` block a write sends: what this drawer loaded, nothing else. */
export function pickExpected(row, watch) {
  if (!row || typeof row !== 'object') return null
  const out = {}
  for (const k of watch) {
    if (Object.prototype.hasOwnProperty.call(row, k) && !blank(row[k])) out[k] = row[k]
  }
  return Object.keys(out).length ? out : null
}

/**
 * Read a 409 from postJson/patchJson. Returns `{stale, gone, fields, current}`
 * or null for any other failure, so a caller can branch on "this is a conflict"
 * without parsing the message itself.
 */
export function conflictFromError(e) {
  if (e?.status !== 409) return null
  const b = e?.body
  if (b?.error === 'line_gone') return { gone: true, stale: false, fields: [], current: null, detail: b.detail }
  if (b?.error === 'stale_record') {
    return { gone: false, stale: true, fields: Array.isArray(b.fields) ? b.fields : [], current: b.current || null, detail: b.detail }
  }
  return null
}

// ── how a changed field is named to the user ────────────────────────────────
// Column names are not what anyone read on screen, so the banner names the
// FIELD in the words the drawer used. A field missing here is shown by its
// column name rather than hidden — an unnamed change is still a change.
const LABEL_TH = {
  billing_type_id: 'รายการ',
  price: 'ค่ารักษา',
  N: 'จำนวน',
  detail: 'รายละเอียด',
  priceadvance: 'เบิกย้อนหลัง',
  claimRef: 'เลขที่เบิก',
  advancePay: 'ผู้จ่าย',
  priceXunit: 'รวม',
  priceDoctor: 'DF',
  priceClinic: 'คลินิก',
  priceActualTotal: 'รวมเบิก',
  priceActualClinic: 'คลินิก (สุทธิ)',
  percentDoctor: 'DF (%)',
  active: 'สถานะรายการ',
  remark: 'หมายเหตุ',
  auto_fee: 'คิดค่าธรรมเนียมอัตโนมัติ',
  appointmentlist_id: 'ประเภทการรักษา',
  doctor_id: 'แพทย์',
}

export const fieldLabel = (f) => LABEL_TH[f] || f

/** "ค่ารักษา 700 → 100" — one changed field, in the words the drawer used. */
export function describeChange({ field, was, now }) {
  const show = (v) => (blank(v) ? '—' : String(v))
  return `${fieldLabel(field)} ${show(was)} → ${show(now)}`
}
